<template>
  <div class="fts-automation-query-display-item">
    <template v-if="Array.isArray(filter.items)">
      <template v-if="['or', 'and', 'not'].includes(filter.type)">
        <div 
          class="automation-query-display-condition" 
          :class="`automation-query-display-condition--${filter.type}`"
        >
          {{filter.type}}
        </div>

        <fts-automation-query-display-item 
          v-for="item in filter.items" 
          :key="item.id" 
          :information="information" 
          :states="states" 
          :filter="item" 
          class="automation-query-display-item--child"
        />
      </template>

      <template v-else>
        <fts-label-group :item="filterData" information />

        <fts-automation-query-display-item 
          v-for="item in filter.items" 
          :key="item.id" 
          :information="filterDataFields" 
          :states="states" 
          :filter="item" 
          class="automation-query-display-item--child"
        />
      </template>
    </template>

    <template v-else>
      <div class="automation-query-display-group">
        <fts-label-group :item="filterData" information />
        
        <div class="automation-query-display-operator">{{filterOperator}}</div>

        <fts-state 
          :states="states" 
          :state="filter.payload" 
          class="automation-query-display-payload" 
          v-if="filter.fieldId === 'state'"
        />

        <div class="automation-query-display-payload" v-else-if="filter.type === 'formula'">
          <fts-formula-display :formula="filter.formula" :variables="variables" />
        </div>

        <template v-else-if="filterPayload">
          <div 
            class="automation-query-display-payload automation-query-display-option"
            :style="{'background-color': getOptionBackgroundColor}" 
            v-if="filterDataType == 27"
          >
            {{filterPayload}}
          </div>

          <div class="automation-query-display-payload" v-else>
            {{filterPayload}}
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import commons from '../../commons/commons';
import queryLogic from '../../commons/queryLogic';

import FTSFormulaDisplay from '../FTSFormulaDisplay';
import FTSLabelGroup from '../../elements/FTSLabelGroup';
import FTSState from '../../elements/FTSState';

export default {
  name: "fts-automation-query-display-item",
  props: {
    information: Array,
    states: Array,
    variables: Array,
    filter: Object
  },
  components: {
    "fts-formula-display": FTSFormulaDisplay,
    "fts-label-group": FTSLabelGroup,
    "fts-state": FTSState
  },
  computed: {
    filterData() {
      if (this.filter && this.filter.fieldId) {
        return this.information.find(x => x.id == this.filter.fieldId);
      }
    },
    filterDataType() {
      return this.filterData && this.filterData.type;
    },
    filterDataFields() {
      if (this.filterData && Array.isArray(this.filterData.fields)) {
        return this.filterData.fields;
      }
      
      return [];
    },
    filterOperator() {
      if (this.filterData && this.filter.operator) {
        const getOperator = (searchType, operator) => {
          const searchTypes = queryLogic.getSearchTypes(searchType);
          return Array.isArray(searchTypes)
            ? searchTypes.find(x => x.fieldName == operator)
            : null;
        }

        const { type, operator: op, fieldId } = this.filter;

        if (type == "formula") {
          const operator = getOperator("formula", op);
          if (operator) return operator.displayName;
        }

        const filterType = queryLogic.getInformationType(this.information, fieldId);
        if (filterType) {
          const operator = getOperator(filterType, op);
          if (operator) return operator.displayName;
        }
      }

      return "(unknown operator)";
    },
    filterPayload() {
      if (this.filter && this.filter.payload) {
        const filterOperator = this.filter.operator;
        const filterPayload = this.filter.payload;

        if (
          this.filterDataType == 1 || 
          ([20, 21].includes(this.filterDataType) && filterOperator == "me")
        ) {
          return "(pass, to-do remove)";
        }

        if ([2, 3, 4].includes(this.filterDataType)) {
          if (filterOperator == "between") {
            let payload = null;

            if (commons.isNumber(filterPayload.min)) {
              payload = {
                min: filterPayload.min.toLocaleString(undefined, {})
              }
            }

            if (commons.isNumber(filterPayload.max)) {
              payload = {
                ...payload,
                max: filterPayload.max.toLocaleString(undefined, {})
              }
            }

            if (payload.min || payload.max) {
              return `${payload.min || '(blank)'} - ${payload.max || '(blank)'}`;
            }
          } else if (
            filterOperator == "gte" && 
            commons.isNumber(filterPayload.min)
          ) {
            return filterPayload.min.toLocaleString(undefined, {});
          } else if (
            filterOperator == "lte" && 
            commons.isNumber(filterPayload.max)
          ) {
            return filterPayload.max.toLocaleString(undefined, {});
          } else if ( commons.isNumber(filterPayload)) {
            return filterPayload.toLocaleString(undefined, {});
          }
        } else if ([5, 6].includes(this.filterDataType)) {
          const dateFormat = (value) => luxon.fromISO(value).toFormat('DD');

          if (filterOperator == "between") {
            let payload = null;

            if (commons.isDateISO_8601(filterPayload.startDate)) {
              payload = {
                startDate: dateFormat(filterPayload.startDate)
              }
            }

            if (commons.isDateISO_8601(filterPayload.endDate)) {
              payload = {
                ...payload,
                endDate: dateFormat(filterPayload.endDate)
              }
            }

            if (payload.startDate || payload.endDate) {
              return `${payload.startDate || '(blank)'} - ${payload.endDate || '(blank)'}`;
            }
          } else if (filterOperator == "within") {
            if (filterPayload && filterPayload.duration) {
              const offsets = queryLogic.getDatePredefinedselection(filterPayload.duration);

              if (Array.isArray(offsets)) {
                const offset = offsets.find(x => x.offset === filterPayload.offset)

                if (offset) {
                  return `${filterPayload.duration} ${offset.displayName}`;
                }
              }
            }
          } else if (
            filterPayload && 
            !commons.isArray(filterPayload) &&
            !commons.isObject(filterPayload)
          ) {
            return dateFormat(filterPayload);
          }
        } else if (
          !commons.isArray(filterPayload) && 
          !commons.isObject(filterPayload) && 
          filterPayload !== null && 
          filterPayload !== undefined
        ) {
          return filterPayload;
        }
      }
    },
    getOptionBackgroundColor() {
      if (this.filterData && Array.isArray(this.filterData.optionList)) {
        return commons.selectOptionBackground({
          optionList: this.filterData.optionList,
          text: this.filterPayload
        });
      }
    }
  }
}
</script>

<style lang="scss">
.fts-automation-query-display-item {
  .automation-query-display-condition {
    display: inline-flex;
    margin-bottom: 0.25rem;
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: 0.5rem;
    background-color: var(--fts-primary);
    color: var(--fts-light);
    &.automation-query-display-condition--or {
      background-color: var(--fts-secondary);
    }
    &.automation-query-display-condition--not {
      background-color: var(--fts-danger);
    }
  }
  .automation-query-display-group {
    display: flex;
    align-items: center;
  }
  .automation-query-display-operator {
    margin-left: 0.375rem;
  }
  .automation-query-display-payload {
    margin-left: 0.375rem;
    &.fts-state > div {
      font-size: 0.875rem;
    }
  }
  .automation-query-display-option {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    border-radius: 0.25rem;
  }
  &.automation-query-display-item--child {
    margin-left: 1.125rem;
  }
  &:not(:last-child) {
    margin-bottom: 0.375rem;
  }
}
</style>